/* global globalThis */
/**
 * SecurityHelper.js
 * Universal AES-256-CBC encryption and decryption compatible with backend .NET SecurityHelper.
 * Key: SahakaarPayrollMasterSecret2026! (32 bytes)
 */

const SECRET_KEY = "SahakaarPayrollMasterSecret2026!";

// Safe Base64 helpers (works in browser and Node/testing)
function safeBtoa(binary) {
  if (typeof window !== "undefined" && window.btoa) return window.btoa(binary);
  if (typeof btoa !== "undefined") return btoa(binary);
  return Buffer.from(binary, "binary").toString("base64");
}

function safeAtob(base64) {
  if (typeof window !== "undefined" && window.atob) return window.atob(base64);
  if (typeof atob !== "undefined") return atob(base64);
  return Buffer.from(base64, "base64").toString("binary");
}

// Helper to convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return safeBtoa(binary);
}

// Helper to convert Base64 to ArrayBuffer
function base64ToArrayBuffer(base64) {
  const binary_string = safeAtob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
}

// Get crypto object
function getCrypto() {
  if (typeof window !== "undefined" && window.crypto && window.crypto.subtle) {
    return window.crypto;
  }
  if (typeof globalThis !== "undefined" && globalThis.crypto && globalThis.crypto.subtle) {
    return globalThis.crypto;
  }
  return null;
}

// Import raw key for WebCrypto
async function importAesKey(cryptoObj) {
  const enc = new TextEncoder();
  const keyBytes = enc.encode(SECRET_KEY);
  return await cryptoObj.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-CBC" },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Encrypts a plain text password using AES-256-CBC.
 * Returns formatted string: 'ENC:<base64(iv + ciphertext)>'
 */
export async function encryptPassword(text) {
  if (!text) return "";
  const str = String(text).trim();
  if (str === "") return "";
  if (str.startsWith("ENC:")) return str;

  try {
    const cryptoObj = getCrypto();
    if (!cryptoObj) {
      // Fallback if WebCrypto not available
      return "ENC:" + (window.btoa ? window.btoa(str) : str);
    }

    const key = await importAesKey(cryptoObj);
    const iv = cryptoObj.getRandomValues(new Uint8Array(16));
    const enc = new TextEncoder();
    const encoded = enc.encode(str);

    const cipherBuffer = await cryptoObj.subtle.encrypt(
      { name: "AES-CBC", iv },
      key,
      encoded
    );

    const combined = new Uint8Array(iv.length + cipherBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipherBuffer), iv.length);

    return "ENC:" + arrayBufferToBase64(combined.buffer);
  } catch (err) {
    console.error("SecurityHelper: Encryption failed:", err);
    return str;
  }
}

/**
 * Decrypts an 'ENC:<base64>' password string back to plain text.
 * If text does not start with 'ENC:', returns it as-is.
 */
export async function decryptPassword(encText) {
  if (!encText) return "";
  const str = String(encText).trim();
  if (!str.startsWith("ENC:")) return str;

  try {
    const cryptoObj = getCrypto();
    if (!cryptoObj) {
      const b64 = str.substring(4);
      return window.atob ? window.atob(b64) : b64;
    }

    const key = await importAesKey(cryptoObj);
    const combinedBuffer = base64ToArrayBuffer(str.substring(4));
    const combinedArray = new Uint8Array(combinedBuffer);

    if (combinedArray.length < 17) return str;

    const iv = combinedArray.slice(0, 16);
    const cipherData = combinedArray.slice(16);

    const decryptedBuffer = await cryptoObj.subtle.decrypt(
      { name: "AES-CBC", iv },
      key,
      cipherData
    );

    const dec = new TextDecoder();
    return dec.decode(decryptedBuffer);
  } catch (err) {
    console.error("SecurityHelper: Decryption failed:", err);
    return encText;
  }
}

/**
 * Compares an entered plain text password against stored (either encrypted or plain) password.
 */
export async function verifyPassword(enteredPassword, storedPassword) {
  if (!enteredPassword) return false;
  const cleanEntered = String(enteredPassword).trim();
  if (!cleanEntered) return false;

  const decryptedStored = await decryptPassword(storedPassword);
  return cleanEntered === (decryptedStored || "").trim();
}

export default {
  encryptPassword,
  decryptPassword,
  verifyPassword,
};
